import { IDictationItem } from 'common/modules/ReactShared';
import { AudioRecorder } from 'common/modules/AudioRecorder';
import { t } from 'i18n';
import { FC, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { SvgAudioPlayer } from 'students/views/shared/components/MediaPlayer';
import { customMediaQuery } from 'students/views/shared/styled';
import styled, { keyframes } from 'styled-components';
import SpeechResultsFeedback from './components/SpeechResultsFeedback';
import useDictationSpeechTaskItem, {
  SpeechItemState
} from './useDictationSpeechTaskItem';

const AUTOPLAY_DELAY = 2000;

interface IProps {
  item: IDictationItem;
  mode: 'listen_repeat' | 'read_speak';
  itemSessionUrl?: string;
  thresholdDecay: number;
  onItemCompleted: (itemId: number, mistakesCount: number) => void;
  onSkip: (itemId: number) => void;
}

const DictationSpeechTaskItem: FC<IProps> = ({
  item,
  mode,
  itemSessionUrl,
  thresholdDecay,
  onItemCompleted,
  onSkip
}) => {
  const {
    state,
    speechResponse,
    errorMessage,
    handleSubmitAudio,
    handleTryAgain,
    handleSkip,
    handleRetryError,
    handleNext
  } = useDictationSpeechTaskItem({
    item,
    itemSessionUrl,
    onItemCompleted,
    onSkip
  });

  const blobRef = useRef<Blob | null>(null);
  const [recorderKey, setRecorderKey] = useState(0);

  const handleRecordChange = useCallback((blob: Blob | null) => {
    blobRef.current = blob;
  }, []);

  const handleSubmitClick = useCallback(() => {
    if (blobRef.current) {
      handleSubmitAudio(blobRef.current);
    }
  }, [handleSubmitAudio]);

  const handleTryAgainClick = useCallback(() => {
    blobRef.current = null;
    setRecorderKey((k) => k + 1);
    handleTryAgain();
  }, [handleTryAgain]);

  const isResultsState =
    state === SpeechItemState.RESULTS_PASS || state === SpeechItemState.RESULTS_FAIL;
  const showRecorder = state === SpeechItemState.IDLE;

  const studentAudioUrl = useMemo(() => {
    if (isResultsState && blobRef.current) {
      return URL.createObjectURL(blobRef.current);
    }
    return undefined;
  }, [isResultsState]);

  useEffect(() => {
    return () => {
      if (studentAudioUrl) URL.revokeObjectURL(studentAudioUrl);
    };
  }, [studentAudioUrl]);

  const submitButton = (
    <SSubmitButton type="button" onClick={handleSubmitClick}>
      {t('frontend.lesson_task.tasks.play.dictation.speech.submit', {
        defaultValue: 'Submit'
      })}
    </SSubmitButton>
  );

  return (
    <SWrapper>
      {/* Audio player for listen_repeat mode */}
      {mode === 'listen_repeat' && showRecorder && (
        <SSvgAudioPlayer
          src={item.audioURL}
          color="#834581"
          progressColor="#fbfcff"
          autoplay
          autoPlayDelay={AUTOPLAY_DELAY}
        />
      )}

      {/* Sentence display for read_speak mode */}
      {mode === 'read_speak' && showRecorder && (
        <SSentenceCard>
          <SSentenceText>{item.sentence}</SSentenceText>
          {item.description && <SDescription>{item.description}</SDescription>}
        </SSentenceCard>
      )}

      {/* Recording UI — AudioRecorder manages its own EMPTY/RECORDING/FINISHED states */}
      {showRecorder && (
        <AudioRecorder
          key={recorderKey}
          mode="speech"
          onRecordChange={handleRecordChange}
          submitButton={submitButton}
        />
      )}

      {/* Evaluating state */}
      {state === SpeechItemState.EVALUATING && (
        <SEvaluatingText>
          {t('frontend.lesson_task.tasks.play.dictation.speech.evaluating', {
            defaultValue: 'Evaluating your pronunciation...'
          })}
        </SEvaluatingText>
      )}

      {/* Results */}
      {isResultsState && speechResponse?.answer && (
        <SpeechResultsFeedback
          answer={speechResponse.answer}
          correct={speechResponse.correct}
          currentThreshold={speechResponse.currentThreshold}
          thresholdDecay={thresholdDecay}
          studentAudioUrl={studentAudioUrl}
          teacherAudioUrl={item.audioURL || undefined}
        />
      )}

      {/* Next button on pass */}
      {state === SpeechItemState.RESULTS_PASS && (
        <SNextButton onClick={handleNext}>
          {t('frontend.lesson_task.tasks.play.dictation.speech.next', {
            defaultValue: 'Next'
          })}
        </SNextButton>
      )}

      {/* Try again button on failure */}
      {state === SpeechItemState.RESULTS_FAIL && (
        <STryAgainButton onClick={handleTryAgainClick}>
          {t('frontend.lesson_task.tasks.play.dictation.speech.try_again', {
            defaultValue: 'Try again'
          })}
        </STryAgainButton>
      )}

      {/* Error state */}
      {state === SpeechItemState.ERROR && (
        <SErrorCard>
          <SErrorIcon>!</SErrorIcon>
          <SErrorTitle>
            {t('frontend.lesson_task.tasks.play.dictation.speech.error_title', {
              defaultValue: 'Something went wrong'
            })}
          </SErrorTitle>
          <SErrorText>{errorMessage}</SErrorText>
          <SErrorActions>
            <SRetryButton onClick={handleRetryError}>
              {t('frontend.lesson_task.tasks.play.dictation.speech.retry', {
                defaultValue: 'Retry'
              })}
            </SRetryButton>
            <SSkipButton onClick={handleSkip}>
              {t('frontend.lesson_task.tasks.play.dictation.speech.skip', {
                defaultValue: 'Skip'
              })}
            </SSkipButton>
          </SErrorActions>
        </SErrorCard>
      )}

      {/* Skip button (available during IDLE only, error has its own skip) */}
      {state === SpeechItemState.IDLE && (
        <SSkipButton onClick={handleSkip}>
          {t('frontend.lesson_task.tasks.play.dictation.speech.skip', {
            defaultValue: 'Skip'
          })}
        </SSkipButton>
      )}
    </SWrapper>
  );
};

export default DictationSpeechTaskItem;

const pulse = keyframes`
  0%, 100% { opacity: 1; }
  50% { opacity: 0.4; }
`;

const SWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  width: 100%;
  gap: 16px;

  ${customMediaQuery('tablet')} {
    width: 557px;
  }
`;

const SSvgAudioPlayer = styled(SvgAudioPlayer)`
  margin-block-end: 24px;
`;

const SSentenceCard = styled.div`
  background: rgba(255, 255, 255, 0.12);
  border-radius: 12px;
  padding: 20px;
  text-align: center;
  width: 100%;
`;

const SSentenceText = styled.div`
  font-size: 1.5rem;
  font-weight: 500;
  color: #fff;
  letter-spacing: 0.3px;
`;

const SDescription = styled.div`
  font-size: 0.875rem;
  color: rgba(255, 255, 255, 0.6);
  margin-top: 8px;
`;

const SEvaluatingText = styled.div`
  font-size: 1.125rem;
  color: #ffa726;
  font-weight: 500;
  padding: 40px 16px;
  text-align: center;
  animation: ${pulse} 1.5s ease-in-out infinite;
`;

const SSubmitButton = styled.button`
  display: flex;
  align-items: center;
  background: #834581;
  color: white;
  border: none;
  border-radius: 16px;
  padding: 12px 24px;
  font-size: 1.125rem;
  font-weight: 700;
  cursor: pointer;
  height: 48px;
`;

const SNextButton = styled.button`
  width: 100%;
  max-width: 300px;
  padding: 12px;
  background: linear-gradient(0deg, #39b54a 6.9%, #27a939 94.83%);
  box-shadow: inset 0 2px 0 #58cd68, inset 0 -2px 0 #0b9444;
  color: white;
  border: none;
  border-radius: 10px;
  font-size: 1.125rem;
  font-weight: 600;
  cursor: pointer;
  margin-top: 8px;
`;

const STryAgainButton = styled.button`
  width: 100%;
  max-width: 300px;
  padding: 12px;
  background: #834581;
  color: white;
  border: none;
  border-radius: 10px;
  font-size: 1.125rem;
  font-weight: 600;
  cursor: pointer;
  margin-top: 8px;
`;

const SSkipButton = styled.button`
  background: rgba(0, 0, 0, 0.2);
  border: none;
  border-radius: 8px;
  color: rgba(255, 255, 255, 0.8);
  font-size: 1rem;
  font-weight: 600;
  padding: 8px 24px;
  cursor: pointer;
  margin-top: 8px;
`;

const SErrorCard = styled.div`
  background: rgba(255, 88, 88, 0.12);
  border: 1px solid rgba(255, 88, 88, 0.25);
  border-radius: 12px;
  padding: 24px;
  text-align: center;
  width: 100%;
`;

const SErrorIcon = styled.div`
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background: rgba(255, 88, 88, 0.2);
  color: #ff5858;
  font-size: 1.25rem;
  font-weight: 700;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 12px;
`;

const SErrorTitle = styled.div`
  color: #fff;
  font-size: 1.25rem;
  font-weight: 600;
  margin-bottom: 6px;
`;

const SErrorText = styled.div`
  color: rgba(255, 255, 255, 0.6);
  font-size: 1rem;
  margin-bottom: 20px;
`;

const SErrorActions = styled.div`
  display: flex;
  gap: 10px;
  justify-content: center;

  > button {
    flex: 1;
    max-width: 140px;
    padding: 10px 24px;
    border: none;
    border-radius: 10px;
    font-size: 1.125rem;
    font-weight: 600;
    cursor: pointer;
    margin-top: 0;
  }
`;

const SRetryButton = styled.button`
  background: #834581;
  color: white;
`;
